import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi, type User, type LoginRequest, type ApiError } from '../lib/api'
import { REPORT_NAV_ROLES } from '../lib/navAccess'

/**
 * Hook for managing authentication state.
 * Uses React Query for caching and automatic refetching.
 */
export function useAuth() {
  const queryClient = useQueryClient()

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['user'],
    queryFn: authApi.getUser,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authApi.login(credentials),
    onSuccess: (data) => {
      if (data.user && !data.status) {
        queryClient.setQueryData(['user'], data.user)
      }
    },
  })

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      queryClient.setQueryData(['user'], null)
      queryClient.clear()
    },
  })

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    error: error as ApiError | null,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error as ApiError | null,
    logout: logoutMutation.mutate,
    logoutAsync: logoutMutation.mutateAsync,
    isLoggingOut: logoutMutation.isPending,
    refetch,
  }
}

/**
 * Hook for checking user permissions based on role.
 */
export function usePermissions() {
  const { user } = useAuth()

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false
    const roleArray = Array.isArray(roles) ? roles : [roles]
    return roleArray.includes(user.role)
  }

  const canAccessAllLeads = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canCreateLeads = (): boolean => {
    return hasRole(['sales_rep', 'operations', 'management', 'super_admin'])
  }

  const canUpdateLeads = (): boolean => {
    return hasRole(['sales_rep', 'operations', 'management', 'super_admin'])
  }

  const canDeleteLeads = (): boolean => {
    return hasRole(['management', 'super_admin'])
  }

  const canTransitionStage = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canAssignLeads = (): boolean => {
    // Mirrors LeadPolicy::assign — reassigning a lead's owner is an
    // ops/management-level action. Sales reps can view/edit their own
    // leads but can't move ownership around.
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canSubmitToFiserv = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canOverrideValidiFI = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canRecheckValidiFI = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canManageUsers = (): boolean => {
    return hasRole(['super_admin'])
  }

  const canManagePricingTemplates = (): boolean => {
    return hasRole(['management', 'super_admin'])
  }

  const canManageScheduleA = (): boolean => {
    return hasRole('super_admin')
  }

  const canManageTicketTypes = (): boolean => {
    return hasRole(['management', 'super_admin'])
  }

  const canManageHelpHub = (): boolean => {
    return hasRole('super_admin')
  }

  const canViewReports = (): boolean => {
    return hasRole([...REPORT_NAV_ROLES])
  }

  const canViewDataHealth = (): boolean => {
    return hasRole(['management', 'super_admin'])
  }

  const canManageGatewayServices = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canViewDeploymentTasks = (): boolean => {
    return hasRole(['support', 'operations', 'management', 'finance', 'super_admin'])
  }

  const canRetryDeploymentTasks = (): boolean => {
    return hasRole(['support', 'operations', 'management', 'super_admin'])
  }

  const canImportMerchants = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canManageAccountGroups = (): boolean => {
    return hasRole(['management', 'super_admin'])
  }

  const canViewMerchantMaintenance = (): boolean => {
    return hasRole(['support', 'operations', 'management', 'super_admin'])
  }

  const canEditMerchantMaintenance = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canViewFinancialReports = (): boolean => {
    return hasRole(['finance', 'super_admin'])
  }

  const canViewMerchantReporting = (): boolean => {
    return hasRole(['support', 'operations', 'management', 'finance', 'super_admin'])
  }

  const canViewChargebacks = (): boolean => {
    return hasRole(['operations', 'management', 'finance', 'super_admin'])
  }

  const canViewChargebackPortfolio = (): boolean => {
    return hasRole(['management', 'finance', 'super_admin'])
  }

  const canRespondChargebacks = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canRecordChargebackOutcome = (): boolean => {
    return hasRole(['management', 'super_admin'])
  }

  const canViewTickets = (): boolean => {
    return hasRole(['support', 'operations', 'management', 'super_admin'])
  }

  const canCreateTickets = (): boolean => {
    return hasRole(['sales_rep', 'support', 'operations', 'management', 'super_admin'])
  }

  const canAssignTickets = (): boolean => {
    return canViewTickets()
  }

  const canResolveTickets = (): boolean => {
    return canViewTickets()
  }

  const canCloseTickets = (): boolean => {
    return hasRole(['management', 'super_admin'])
  }

  const canViewRestrictedDocuments = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canUploadVAR = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canDeleteDocuments = (): boolean => {
    return hasRole('super_admin')
  }

  const canRequestDocuments = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  /**
   * Whether the user can mark an upload `is_restricted` (hides it from
   * sales_rep / support / finance) — mirrors DocumentPolicy::uploadRestricted.
   */
  const canUploadRestricted = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  /**
   * Whether the user can email a document to an external recipient —
   * mirrors DocumentPolicy::emailDocument.
   */
  const canEmailDocuments = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  /**
   * Whether the user can generate pre-filled documents from lead data —
   * mirrors DocumentPolicy::generatePdf.
   */
  const canGeneratePdf = (): boolean => {
    return hasRole(['operations', 'management', 'super_admin'])
  }

  const canUseStatementAnalysis = (): boolean => {
    return hasRole(['sales_rep', 'operations', 'management', 'finance', 'super_admin'])
  }

  const canCreateHelpArticles = (): boolean => {
    return hasRole(['sales_rep', 'operations', 'management', 'super_admin'])
  }

  return {
    user,
    hasRole,
    canAccessAllLeads,
    canCreateLeads,
    canUpdateLeads,
    canDeleteLeads,
    canTransitionStage,
    canAssignLeads,
    canSubmitToFiserv,
    canOverrideValidiFI,
    canRecheckValidiFI,
    canManageUsers,
    canManagePricingTemplates,
    canManageScheduleA,
    canManageTicketTypes,
    canManageHelpHub,
    canViewReports,
    canViewDataHealth,
    canManageGatewayServices,
    canViewDeploymentTasks,
    canRetryDeploymentTasks,
    canViewMerchantMaintenance,
    canEditMerchantMaintenance,
    canViewFinancialReports,
    canViewMerchantReporting,
    canViewChargebacks,
    canViewChargebackPortfolio,
    canRespondChargebacks,
    canRecordChargebackOutcome,
    canViewTickets,
    canCreateTickets,
    canImportMerchants,
    canManageAccountGroups,
    canAssignTickets,
    canResolveTickets,
    canCloseTickets,
    canViewRestrictedDocuments,
    canUploadVAR,
    canDeleteDocuments,
    canRequestDocuments,
    canUploadRestricted,
    canEmailDocuments,
    canGeneratePdf,
    canUseStatementAnalysis,
    canCreateHelpArticles,
  }
}

export type { User, LoginRequest }
